import { useTu } from "@src/appV2/i18n/useTu";
import {
  type BreakPolicyRule,
  type UseGetFacilityProfileResponse,
} from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { useUpdateFacilityProfile } from "@src/appV2/FacilityProfile/api/useUpdateFacilityProfile";
import { useGetFacilityNotes } from "@src/appV2/FacilityRules/api/useGetFacilityNotes";
import { useUpdateFacilityNotes } from "@src/appV2/FacilityRules/api/useUpdateFacilityNotes";
import {
  DEFAULT_MANDATORY_BREAK_POLICY_NOTE,
  DEFAULT_REST_BREAK_POLICY_NOTE,
  NoteFieldIdentifiers,
} from "@src/appV2/FacilityRules/constants";
import { type FacilityNote } from "@src/appV2/FacilityRules/types";
import { useToast } from "@src/appV2/lib";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { useState } from "react";

import { type SettingsPermissions } from "../types";

interface UseBreakPoliciesParams {
  facilityProfile: UseGetFacilityProfileResponse | undefined;
  permissions: SettingsPermissions;
  workplaceId: string;
  onSettingsChanged: () => void;
}

function findNoteByIdentifier(
  notes: FacilityNote[] | undefined,
  identifier: string
): FacilityNote | undefined {
  return notes?.find((note) => note.identifier === identifier);
}

function updateNoteInList(
  notes: FacilityNote[],
  identifier: string,
  newNoteText: string
): FacilityNote[] {
  return notes.map((note) =>
    note.identifier === identifier ? { ...note, note: newNoteText } : note
  );
}

export function useBreakPolicies({
  facilityProfile,
  permissions,
  workplaceId,
  onSettingsChanged,
}: UseBreakPoliciesParams) {
  const { tu } = useTu("settings");

  const { showSuccessToast, showErrorToast } = useToast();

  const [requiresLunchBreak, setRequiresLunchBreak] = useState(
    facilityProfile?.requiresLunchBreak ?? false
  );
  const [providesRestBreaks, setProvidesRestBreaks] = useState(
    facilityProfile?.providesRestBreaks ?? false
  );
  const [isAutoDeclinationEnabled, setIsAutoDeclinationEnabled] = useState(
    facilityProfile?.extraWorkedTimeConfig?.BREAK?.isAutoDeclinationEnabled ?? false
  );

  const mandatoryBreakPolicyModalState = useModalState(ModalStatus.CLOSED);
  const restBreakPolicyModalState = useModalState(ModalStatus.CLOSED);
  const autoDeclineReasonModalState = useModalState(ModalStatus.CLOSED);

  const { data: facilityNotes, refetch: refetchNotes } = useGetFacilityNotes(workplaceId, {
    enabled: Boolean(workplaceId),
  });

  const { isLoading: isFacilityProfileUpdating, mutateAsync: updateFacilityProfile } =
    useUpdateFacilityProfile();
  const { isLoading: isNotesUpdating, mutateAsync: updateFacilityNotes } =
    useUpdateFacilityNotes(workplaceId);

  const isBreakUpdating = isFacilityProfileUpdating || isNotesUpdating;

  const mandatoryBreakNote = findNoteByIdentifier(
    facilityNotes,
    NoteFieldIdentifiers.MANDATORY_BREAK_POLICY
  );
  const restBreakNote = findNoteByIdentifier(facilityNotes, NoteFieldIdentifiers.REST_BREAK_POLICY);

  const mandatoryBreakPolicyText = mandatoryBreakNote?.note ?? DEFAULT_MANDATORY_BREAK_POLICY_NOTE;
  const restBreakPolicyText = restBreakNote?.note ?? DEFAULT_REST_BREAK_POLICY_NOTE;
  const autoDeclinationReason =
    facilityProfile?.extraWorkedTimeConfig?.BREAK?.autoDeclinationReason;

  const isCaliforniaFacility = facilityProfile?.fullAddress?.state === "California";
  const isAutoDeclineVisible = requiresLunchBreak;

  const handleMandatoryBreakToggle = async (checked: boolean) => {
    if (checked) {
      mandatoryBreakPolicyModalState.openModal();
      return;
    }

    setRequiresLunchBreak(false);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        requiresLunchBreak: false,
        requiresLunchBreakSince: null,
      });
      showSuccessToast(tu("Mandatory break disabled successfully."));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update mandatory break setting."));
      setRequiresLunchBreak(true);
    }
  };

  const handleMandatoryBreakPolicySave = async (policyText: string) => {
    try {
      if (facilityNotes && policyText !== mandatoryBreakNote?.note) {
        await updateFacilityNotes({
          oldFacilityNotes: facilityNotes,
          newFacilityNotes: updateNoteInList(
            facilityNotes,
            NoteFieldIdentifiers.MANDATORY_BREAK_POLICY,
            policyText
          ),
          analyticsMetadata: { workplaceId, isAdmin: permissions.isAdmin },
        });
      }

      if (!requiresLunchBreak) {
        await updateFacilityProfile({
          facilityId: workplaceId,
          requiresLunchBreak: true,
          requiresLunchBreakSince: new Date().toISOString(),
        });
      }

      setRequiresLunchBreak(true);
      mandatoryBreakPolicyModalState.closeModal();
      showSuccessToast(tu("Mandatory break policy updated successfully."));
      onSettingsChanged();
      void refetchNotes();
    } catch {
      showErrorToast(tu("Failed to update mandatory break policy."));
    }
  };

  const handleRestBreaksToggle = async (checked: boolean) => {
    if (checked) {
      restBreakPolicyModalState.openModal();
      return;
    }

    setProvidesRestBreaks(false);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        providesRestBreaks: false,
        providesRestBreaksSince: null,
      });
      showSuccessToast(tu("Rest breaks disabled successfully."));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update rest break setting."));
      setProvidesRestBreaks(true);
    }
  };

  const handleRestBreakPolicySave = async (policyText: string) => {
    try {
      if (facilityNotes && policyText !== restBreakNote?.note) {
        await updateFacilityNotes({
          oldFacilityNotes: facilityNotes,
          newFacilityNotes: updateNoteInList(
            facilityNotes,
            NoteFieldIdentifiers.REST_BREAK_POLICY,
            policyText
          ),
          analyticsMetadata: { workplaceId, isAdmin: permissions.isAdmin },
        });
      }

      if (!providesRestBreaks) {
        await updateFacilityProfile({
          facilityId: workplaceId,
          providesRestBreaks: true,
          providesRestBreaksSince: new Date().toISOString(),
        });
      }

      setProvidesRestBreaks(true);
      restBreakPolicyModalState.closeModal();
      showSuccessToast(tu("Rest break policy updated successfully."));
      onSettingsChanged();
      void refetchNotes();
    } catch {
      showErrorToast(tu("Failed to update rest break policy."));
    }
  };

  const handleAutoDeclineToggle = async (checked: boolean) => {
    if (checked) {
      autoDeclineReasonModalState.openModal();
      return;
    }

    setIsAutoDeclinationEnabled(checked);
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        extraWorkedTimeConfig: {
          BREAK: {
            autoDeclinationReason,
            isAutoDeclinationEnabled: checked,
          },
        },
      });
      showSuccessToast(
        checked
          ? tu("Auto-decline break requests enabled successfully.")
          : tu("Auto-decline break requests disabled successfully.")
      );
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update auto-decline setting."));
      setIsAutoDeclinationEnabled(!checked);
    }
  };

  const handleBreakPolicySave = async (rules: BreakPolicyRule[]): Promise<boolean> => {
    try {
      await updateFacilityProfile({ facilityId: workplaceId, breakPolicies: rules });
      showSuccessToast(tu("Mandatory break rules updated successfully."));
      onSettingsChanged();
      return true;
    } catch {
      showErrorToast(tu("Failed to update mandatory break rules."));
      return false;
    }
  };

  const handleAutoDeclineReasonSave = async (reason: string) => {
    try {
      await updateFacilityProfile({
        facilityId: workplaceId,
        extraWorkedTimeConfig: {
          BREAK: { isAutoDeclinationEnabled: true, autoDeclinationReason: reason },
        },
      });
      setIsAutoDeclinationEnabled(true);
      autoDeclineReasonModalState.closeModal();
      showSuccessToast(tu("Declination reason updated successfully."));
      onSettingsChanged();
    } catch {
      showErrorToast(tu("Failed to update declination reason."));
    }
  };

  return {
    autoDeclinationReason,
    autoDeclineReasonModalState,
    breakPolicies: facilityProfile?.breakPolicies,
    handleAutoDeclineReasonSave,
    handleAutoDeclineToggle,
    handleBreakPolicySave,
    handleMandatoryBreakPolicySave,
    handleMandatoryBreakToggle,
    handleRestBreakPolicySave,
    handleRestBreaksToggle,
    isAutoDeclineVisible,
    isAutoDeclinationEnabled,
    isBreakUpdating,
    isCaliforniaFacility,
    mandatoryBreakPolicyModalState,
    mandatoryBreakPolicyText,
    providesRestBreaks,
    requiresLunchBreak,
    restBreakPolicyModalState,
    restBreakPolicyText,
  };
}
